import React from 'react'
import { useTranslation } from 'react-i18next'

import { Calendar } from 'primereact/calendar'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { ConfigurableComponent } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config'
import { useDeliveryForecastItemById } from '../../data'

type Props = {
  deliveryForecastId: string
  itemId: string
}

export const DeliveryScheduleDetailsForm: React.FC<Props> = ({
  deliveryForecastId,
  itemId,
}) => {
  const { data: deliveryForecastItem, isLoading } = useDeliveryForecastItemById(
    deliveryForecastId,
    itemId,
  )
  const { uiConfig: config } = useConfigContext()

  const { i18n } = useTranslation()
  const { language } = i18n

  // ************************************************************
  // components
  // ************************************************************
  // Position
  const positionLabel = <label className="text-sm" htmlFor="position"></label>

  const positionComponent = (
    <InputText
      id="position"
      value={deliveryForecastItem?.Position}
      className="w-full"
      disabled
    />
  )

  const positionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={positionComponent}
      label={positionLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label className="text-sm" htmlFor="buyerProductId"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      value={deliveryForecastItem?.BuyerProductId}
      className="w-full"
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label className="text-sm" htmlFor="buyerProductDescription"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      value={deliveryForecastItem?.BuyerProductDescription}
      className="w-full"
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationProductId
  const confirmationProductIdLabel = (
    <label className="text-sm" htmlFor="confirmationProductId"></label>
  )

  const confirmationProductIdComponent = (
    <InputText
      id="confirmationProductId"
      value={deliveryForecastItem?.ConfirmationProductId}
      className="w-full"
      disabled
    />
  )

  const confirmationProductIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={confirmationProductIdComponent}
      label={confirmationProductIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // scheduleLineQuantity
  const scheduleLineQuantityLabel = (
    <label className="text-sm w-full" htmlFor="scheduleLineQuantity"></label>
  )

  const scheduleLineQuantityComponent = (
    <InputNumber
      id="scheduleLineQuantity"
      className="w-full"
      value={deliveryForecastItem?.ScheduleLineQuantity}
      locale={language}
      disabled
    />
  )

  const confirmationCodeWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={scheduleLineQuantityComponent}
      label={scheduleLineQuantityLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // cummulativeQuantityReceived
  const cummulativeQuantityReceivedLabel = (
    <label className="text-sm" htmlFor="cummulativeQuantityReceived"></label>
  )

  const cummulativeQuantityReceivedComponent = (
    <InputNumber
      id="cummulativeQuantityReceived"
      className="w-full"
      value={deliveryForecastItem?.CummulativeQuantityReceived}
      locale={language}
      disabled
    />
  )

  const cummulativeQuantityReceivedWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={cummulativeQuantityReceivedComponent}
      label={cummulativeQuantityReceivedLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // modificationDate
  const modificationDateLabel = (
    <label className="text-sm" htmlFor="modificationDate"></label>
  )

  const modificationDateComponent = (
    <div id="modificationDate">
      {deliveryForecastItem?.LastReceiving?.Date ? (
        <Calendar
          id="modificationDate"
          value={new Date(deliveryForecastItem?.LastReceiving?.Date)}
          className="w-full"
          locale={language}
          disabled
        />
      ) : (
        <InputText id="modificationDate" value="" className="w-full" disabled />
      )}
    </div>
  )

  const modificationDateWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={modificationDateComponent}
      label={modificationDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // lastReceivingQuantity
  const lastReceivingQuantityLabel = (
    <label className="text-sm" htmlFor="lastReceivingQuantity"></label>
  )

  const lastReceivingQuantityComponent = (
    <InputNumber
      id="lastReceivingQuantity"
      className="w-full"
      value={deliveryForecastItem?.LastReceiving?.Quantity}
      locale={language}
      disabled
    />
  )

  const lastReceivingQuantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={lastReceivingQuantityComponent}
      label={lastReceivingQuantityLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // plant
  const plantLabel = <label className="text-sm" htmlFor="plant"></label>

  const plantComponent = (
    <InputText
      id="plant"
      value={deliveryForecastItem?.Plant}
      className="w-full"
      disabled
    />
  )

  const plantWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={plantComponent}
      label={plantLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // unloadingPoint
  const unloadingPointLabel = (
    <label className="text-sm" htmlFor="unloadingPoint"></label>
  )

  const unloadingPointComponent = (
    <InputText
      id="unloadingPoint"
      value={deliveryForecastItem?.UnloadingPoint}
      className="w-full"
      disabled
    />
  )

  const unloadingPointWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.deliveryScheduleDetails}
      component={unloadingPointComponent}
      label={unloadingPointLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  return (
    <>
      <div className="formgrid grid">
        {/* position */}
        {positionWrapper}

        {/* buyerProductId */}
        {buyerProductIdWrapper}

        {/* buyerProductDescription */}
        {buyerProductDescriptionWrapper}

        {/* confirmationProductId */}
        {confirmationProductIdWrapper}

        {/* plant */}
        {plantWrapper}
      </div>

      <div className="formgrid grid">
        {/* confirmationCode */}
        {confirmationCodeWrapper}

        {/* cummulativeQuantityReceived */}
        {cummulativeQuantityReceivedWrapper}

        {/* modificationDate */}
        {modificationDateWrapper}

        {/* lastReceivingQuantity */}
        {lastReceivingQuantityWrapper}

        {/* unloadingPoint */}
        {unloadingPointWrapper}
      </div>
    </>
  )
}
